import { AdvanceShareStyleAssetDefinition, AdvanceShareStyleAssetTypeList } from '@/models/advance-share-style-definition.model';
import { toastErrorMessage } from '@/utils/toasterMessage';
import React, { useState } from 'react';
import {
  Panel,
  Table,
  Button,
  Modal,
  Form,
  Schema,
  SelectPicker,
  Checkbox,
  Stack
} from 'rsuite';

const { Column, HeaderCell, Cell } = Table;

interface AssetDefinitionPanelProps {
  value: AdvanceShareStyleAssetDefinition[];
  onChange: (next: AdvanceShareStyleAssetDefinition[]) => void;
  disabled?: boolean;
}

const AssetDefinitionPanel: React.FC<AssetDefinitionPanelProps> = ({ value, onChange, disabled = false }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingRowIndex, setEditingRowIndex] = useState<number | undefined>(undefined);
  const [editingAsset, setEditingAsset] = useState<AdvanceShareStyleAssetDefinition | undefined>(undefined);

  const model = Schema.Model({
    key: Schema.Types.StringType().isRequired('Key is required'),
    type: Schema.Types.NumberType().isRequired('Type is required'),
    required: Schema.Types.BooleanType()
  });

  const handleSubmit = (formValue: AdvanceShareStyleAssetDefinition) => {
      if(value.some((item, index) => item.key === formValue.key && index !== editingRowIndex)){
          // validate unique key
          toastErrorMessage('Asset key must be unique');
          return;
      }

      if (editingRowIndex != undefined) {
        // update existing
        const updated = [...value];
        updated[editingRowIndex] = formValue;
        onChange(updated);
    } else {
        // add new
        onChange([...value, formValue]);
    }
    setShowModal(false);
    setEditingAsset(undefined);
  };
  
  const handleFormChange = (value: Record<string, any>) => {
    setEditingAsset(prevFormValue => ({
      ...prevFormValue,
      ...value
    }));
  };

  const handleEditRow = (rowData: AdvanceShareStyleAssetDefinition, rowIndex?: number) => {
    setEditingAsset(rowData);
    setShowModal(true);
    setEditingRowIndex(rowIndex);
  };

  const handleDeleteRow = (rowIndex?: number) => {
    const updated = [...value];
    updated.splice(rowIndex!, 1);
    onChange(updated);
  };
  
  return (
    <>
      <Panel
        header={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Assets</span>
            <Button appearance="primary" disabled = {disabled} onClick={() => {
                setShowModal(true);
                setEditingAsset({ key: undefined, type: undefined, required: false });
                setEditingRowIndex(undefined);
            }}>
              Add Asset
            </Button>
          </div>
        }
        bordered
      >
        <Table data={value} autoHeight bordered>
          <Column flexGrow={1}>
            <HeaderCell>Key</HeaderCell>
            <Cell dataKey="key" />
          </Column>
          <Column flexGrow={1}>
            <HeaderCell>Type</HeaderCell>
            <Cell>
              {rowData =>
                AdvanceShareStyleAssetTypeList.findLast(t => t.value === rowData.type)?.label
              }
            </Cell>
          </Column>
          <Column width={100}>
            <HeaderCell>Required</HeaderCell>
            <Cell>{rowData => (rowData.required ? 'Yes' : 'No')}</Cell>
          </Column>
          <Column width={120}>
            <HeaderCell>Actions</HeaderCell>
            <Cell>
              {(rowData: AdvanceShareStyleAssetDefinition, rowIndex?: number) => (
                <Stack spacing={6}>
                    <Button 
                    size="xs" 
                    appearance='ghost' 
                    disabled = {disabled}
                    onClick={() => handleEditRow(rowData, rowIndex)} >
                        Edit
                    </Button>
                    <Button
                        color='red'
                        appearance='ghost'
                        size="xs"
                        disabled = {disabled}
                        onClick={() => handleDeleteRow(rowIndex)}
                    >
                    X
                    </Button>
                </Stack>
              )}
            </Cell>
          </Column>
        </Table>
      </Panel>

      <Modal open={showModal} onClose={() => setShowModal(false)}>
        <Modal.Header>
          <Modal.Title>
            {editingRowIndex == undefined ? 'Add Asset' : 'Edit Asset'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            fluid
            model={model}
            formValue={editingAsset}
            onChange={handleFormChange}
            onSubmit={formValue => handleSubmit(formValue as AdvanceShareStyleAssetDefinition)}
          >
            <Form.Group controlId="key">
              <Form.Label>Key</Form.Label>
              <Form.Control name="key" />
            </Form.Group>
            <Form.Group controlId="type">
            <Form.Label>Type</Form.Label>
              <Form.Control
                name="type"
                accepter={SelectPicker}
                data={AdvanceShareStyleAssetTypeList}
                block
              />
            </Form.Group>
            <Form.Group controlId="required">
              <Form.Label>Required</Form.Label>
              <Form.Control name="required" accepter={Checkbox} 
                  checked={!!editingAsset?.required} 
                  onChange={(val, chk) => setEditingAsset(pre => ({ ...pre, required: chk })) }               
              />
            </Form.Group>
            <Button appearance="primary" type="submit">
              Save
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AssetDefinitionPanel;
